<template>
  <SrpDetailsSummary class="collab-details-form-wrap" :isOpened="isOpened" @update:isOpened="isOpened = $event">
    <template #heading>
      <div>
        <h2 class="global-h2">
          <span><span>Work Agreement</span></span>
        </h2>
        <div v-if="!isOpened">
          <div style="margin-top: 10px">Ready to formalize the details?</div>
          <SrpButton :color="getDetailsButtonColor" :fill="getDetailsButtonOutline" size="small" style="margin-top: 10px">Specify Collab Details</SrpButton>
        </div>
      </div>
    </template>
    <template #details>
      <div style="margin-bottom: 20px; border-bottom: 1px rgba(0, 0, 0, 0.15) solid"></div>

      <!-- Collab details form -->
      <div
        :class="{
          'collab-details-form': true,
          'collab-details-form--disabled': isSaving,
        }"
      >
        <div v-if="!props.customCollabClientPayload.creatorCollabInputs.agreedToDate">
          <SrpButton class="collab-details-form__ai-button" size="small" fill="outlined" @click="$emit('fillWithAI')" :disabled="isSaving" :isDisabled="props.isFillingWithAI">
            <template #icon>
              <LoadingIcon v-if="props.isFillingWithAI" :size="17" />
              <IconEmbedded v-else name="ai_3" :size="17" />
            </template>
            Fill from Messages
          </SrpButton>
          <InfoIconWithTooltip :width="350" style="margin-left: 10px"
            >Uses AI to fill in the details from the messages.<br /><b>Note:</b> This will only fill empty fields (it won't overwrite your input)
          </InfoIconWithTooltip>
        </div>

        <div class="collab-details-form__form-itself">
          <!-- Title and form element -->
          <div class="title-and-form-element collab-details-form__title-and-form-element">
            <div class="global-h4 title-and-form-element__title">Title</div>
            <input
              class="global-text-input title-and-form-element__form-element"
              type="text"
              v-model="props.customCollabClientPayload.customerCollabInputs.title"
              :disabled="isCompleted || props.isFillingWithAI"
            />
          </div>
          <!-- / Title and form element -->

          <!-- Title and form element -->
          <div class="title-and-form-element collab-details-form__title-and-form-element">
            <div class="global-h4 title-and-form-element__title">What's the purpose of the work?</div>
            <textarea
              class="global-textarea title-and-form-element__form-element title-and-form-element__form-element--textarea"
              placeholder="Ex. Location names or context, your social media handle, etc..."
              v-model="props.customCollabClientPayload.customerCollabInputs.purpose"
              :disabled="isCompleted || props.isFillingWithAI"
            ></textarea>
          </div>
          <!-- / Title and form element -->

          <!-- Title and form element -->
          <div class="title-and-form-element collab-details-form__title-and-form-element">
            <div class="global-h4 title-and-form-element__title">Deliverables</div>
            <textarea
              class="global-textarea title-and-form-element__form-element title-and-form-element__form-element--textarea"
              type="text"
              placeholder="Ex. Location names or context, your social media handle, etc..."
              v-model="props.customCollabClientPayload.customerCollabInputs.deliverables"
              :disabled="isCompleted || props.isFillingWithAI"
            ></textarea>
          </div>
          <!-- / Title and form element -->

          <!-- Title and form element -->
          <div class="title-and-form-element collab-details-form__title-and-form-element collab-details-form__title-and-form-element--half-width">
            <div class="global-h4 title-and-form-element__title">Payment</div>
            <!-- Input with dollar sign -->
            <div class="input-with-dollar-sign title-and-form-element__form-element">
              <input
                class="global-text-input input-with-dollar-sign__input"
                type="number"
                v-model="props.customCollabClientPayload.customerCollabInputs.paymentAmount"
                :disabled="isCompleted || props.isFillingWithAI"
              />
            </div>
            <!-- / Input with dollar sign -->
          </div>
          <!-- / Title and form element -->

          <!-- Title and form element -->
          <div class="title-and-form-element collab-details-form__title-and-form-element collab-details-form__title-and-form-element--half-width">
            <div class="global-h4 title-and-form-element__title">Other Compensation</div>
            <input
              class="global-text-input title-and-form-element__form-element"
              style="margin-bottom: 5px"
              v-for="(_, index) in otherCompsInputsVisible"
              type="text"
              placeholder="e.g. Hotel stay"
              v-model="props.customCollabClientPayload.customerCollabInputs.otherComp[index]"
              :disabled="isCompleted || props.isFillingWithAI"
            />
            <LinkWithIcon
              style="margin: 0 0 0 -5px"
              isDottedUnderline
              iconInset="auto -3px auto auto"
              @click="
                () => {
                  if (otherCompsInputsVisible < maxOtherCompInputs) {
                    otherCompsInputsVisible += 1;
                  }
                }
              "
              :isDisabled="otherCompsInputsVisible >= maxOtherCompInputs || isCompleted || props.isFillingWithAI"
            >
              <template #icon><IconEmbedded name="plus_3" :size="18" /></template>
              <span>add another</span>
            </LinkWithIcon>
          </div>
          <!-- / Title and form element -->

          <!-- Title and form element -->
          <div class="title-and-form-element collab-details-form__title-and-form-element collab-details-form__title-and-form-element--half-width">
            <div class="global-h4 title-and-form-element__title">Due Date</div>
            <input
              class="global-text-input title-and-form-element__form-element"
              type="date"
              v-model="props.customCollabClientPayload.customerCollabInputs.dueDate"
              :disabled="isCompleted || props.isFillingWithAI"
            />
          </div>
          <!-- / Title and form element -->

          <!-- Title and form element -->
          <div class="title-and-form-element collab-details-form__title-and-form-element collab-details-form__title-and-form-element--half-width">
            <div class="global-h4 title-and-form-element__title">Activity Date</div>
            <input
              class="global-text-input title-and-form-element__form-element"
              type="date"
              v-model="props.customCollabClientPayload.customerCollabInputs.activityDate"
              :disabled="isCompleted || props.isFillingWithAI"
            />
          </div>
          <!-- / Title and form element -->

          <!-- Title and form element -->
          <div class="title-and-form-element collab-details-form__title-and-form-element">
            <div class="global-h4 title-and-form-element__title">Additional Notes</div>
            <textarea
              class="global-textarea title-and-form-element__form-element title-and-form-element__form-element--textarea"
              placeholder="Any other details..."
              v-model="props.customCollabClientPayload.customerCollabInputs.otherNotes"
              :disabled="isCompleted || props.isFillingWithAI"
            ></textarea>
          </div>
          <!-- / Title and form element -->
        </div>

        <div v-if="!isCompleted" class="collab-details-form__buttons-panel">
          <SrpButton @click="$emit('save')" :isDisabled="isSaving || props.isFillingWithAI" :fill="canSendToCreator ? 'outlined' : 'solid'" :size="screenSize === 'mobile' ? 'small' : 'medium'">
            <template #icon>
              <LoadingIcon v-if="isSaving" :size="20" />
              <IconEmbedded v-else name="save_2-5" :size="20" />
            </template>
            Save
          </SrpButton>
          <div v-if="!props.customCollabClientPayload.customerCollabInputs.formalizedDate">
            <SrpButton
              :fill="canSendToCreator ? 'solid' : 'outlined'"
              :isDisabled="isSaving || props.isFillingWithAI || !props.customCollabClientPayload.customerCollabInputs.creatorId"
              @click="sendToCreatorsClicked"
              style="margin-right: 8px"
              :size="screenSize === 'mobile' ? 'small' : 'medium'"
            >
              <template #icon><IconEmbedded name="send_2-5" :size="20" /></template>
              {{ "Send for Creator Acceptance" }}
            </SrpButton>
            <InfoIconWithTooltip :width="250">Asks the Creator to formally agree to the details. Edits can still be made after as needed, and the creator will be notified.</InfoIconWithTooltip>
          </div>
          <div v-else-if="!props.customCollabClientPayload.creatorCollabInputs.agreedToDate"><b>Sent to creator for formal acceptance</b></div>
          <div v-else>
            <NoteWithIcon size="tiny" :isTransparentBg="true" color="gray">
              <template #icon><IconEmbedded name="info-simple_4" :size="18" /></template>
              <template #text><div>The creator will be notified of updates.</div></template>
            </NoteWithIcon>
          </div>

          <div v-if="!props.customCollabClientPayload.customerCollabInputs.creatorId">* You can send for acceptance after the creator accepts the invite.</div>
          <div v-if="showRequiredFieldsError" class="global-error-string">Fill in Title, Purpose, Deliverables and one of the Payment fields.</div>
        </div>
      </div>
      <!-- / Collab details form -->
    </template>
  </SrpDetailsSummary>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, inject, Ref } from "vue";
import { hasAnyCustomerDetailsAdded, customerRequiredFieldsSet } from "@helpers/CustomCollabsHelper";

// Components
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";

// Types
import { CustomCollabClientPayload } from "@contracts/customCollabs";
import { ScreenSize } from "@contracts/screenSize";
import type { SrpButtonColor, SrpButtonFill } from "@components/ui/SrpButton.vue";

// Global variables & injections
const screenSize = inject("screenSize") as Ref<ScreenSize>;

const props = withDefaults(
  defineProps<{
    customCollabClientPayload: CustomCollabClientPayload | null;
    isSaving?: boolean;
    isFillingWithAI?: boolean;
    messagesCount?: number;
  }>(),
  {
    customCollabClientPayload: null,
    isSaving: false,
    isFillingWithAI: false,
    messagesCount: 0,
  }
);

const emit = defineEmits<{
  (e: "save"): void;
  (e: "showSendForCreatorAcceptance"): void;
  (e: "fillWithAI"): void;
}>();

const isOpened = ref<boolean>(false);
onMounted(() => {
  if (hasAnyCustomerDetailsAdded(props.customCollabClientPayload?.customerCollabInputs, true)) {
    isOpened.value = true;
  }
});

// Send to Creator ===========================================================
const showRequiredFieldsError = ref<boolean>(false);
function sendToCreatorsClicked() {
  if (canSendToCreator.value) {
    emit("showSendForCreatorAcceptance");
  } else {
    showRequiredFieldsError.value = true;
  }
}

const canSendToCreator = computed<boolean>(() => {
  return customerRequiredFieldsSet(props.customCollabClientPayload?.customerCollabInputs);
});

watch(canSendToCreator, newValue => {
  if (newValue) {
    showRequiredFieldsError.value = false;
  }
});

// Check Is Completed =========================================================
const isCompleted = computed<boolean>(() => {
  return props.customCollabClientPayload.customerCollabInputs.markedCompleteDate ? true : false;
});

// Other compensation inputs ==================================================
const maxOtherCompInputs = 10;
const otherCompsInputsVisible = ref<number>(0);

onMounted(() => {
  otherCompsInputsVisible.value = props.customCollabClientPayload.customerCollabInputs.otherComp?.length || 1;
});

// Button color based on messages count
const getDetailsButtonColor = computed<SrpButtonColor>(() => {
  // Note: We could also time base this
  if (props.messagesCount > 10) return "orange";
  return "teal";
});

const getDetailsButtonOutline = computed<SrpButtonFill>(() => {
  return props.messagesCount < 3 ? "outlined" : "solid";
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Input with dollar sign =====================================================
.input-with-dollar-sign {
  position: relative;
  z-index: 0;

  &::before {
    content: "$";
    position: absolute;
    inset: 9px auto auto 13px;
    user-select: none;
    pointer-events: none;
  }

  &__input {
    padding-left: 23px;
  }
}

// Title and form element =====================================================
.title-and-form-element {
  &__title {
    margin-bottom: 8px;
    font-weight: 500;
  }

  &__form-element {
    &--textarea {
      height: 70px;
      min-height: 70px;
    }
  }

  &__add-more-link {
  }
}

// Collab details form ========================================================
.collab-details-form {
  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__ai-button {
    margin-bottom: 24px;
  }

  &__form-itself {
    gap: 21px $desktop-wide-grid-gap-width;
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
  }

  &__title-and-form-element {
    width: 100%;

    &--half-width {
      width: calc(50% - $desktop-wide-grid-gap-width / 2);
    }
  }

  &__buttons-panel {
    gap: 12px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .collab-details-form {
    &__title-and-form-element {
      &--half-width {
        width: 100%;
      }
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .collab-details-form {
    &__title-and-form-element {
      &--half-width {
        width: 100%;
      }
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .collab-details-form {
    &__title-and-form-element {
      &--half-width {
        width: 100%;
      }
    }
  }
}

// Collab details form wrap ===================================================
.collab-details-form-wrap {
  padding: 20px 25px 24px;
  border-radius: 6px;
  background: #f0f0f0;
}
</style>
